import {HttpService} from '../services/core/http.service'
import {SyncItem} from '../interfaces/core/sync-item';
import {map} from 'rxjs/operators';
import {inject} from '@angular/core';
import {AdapterOptions} from '../interfaces/adapter/adapter-options';
import {SyncStatusItem} from '../interfaces/core/sync-status-item';
import {SyncException} from '../exceptions/sync.exception';
import {RequestCancelledError} from '../exceptions/request-cancelled.error';

export class DefaultAdapter<T extends SyncItem> {

  private readonly _api: string[];
  private readonly _foreignKey: keyof T;
  protected readonly _httpService: HttpService;
  protected readonly _fromObject: (item: any) => T = (item: any) => item as T;
  protected readonly _fromArray: (items: any[]) => T[] = (items: any[]) => items as T[];
  protected readonly _toRemote: (item: T) => any = (item: T) => item;
  protected readonly _toArray: (items: T[]) => any[] = (items: T[]) => items;

  /**
   *
   * @param adapterOptions
   */
  constructor(adapterOptions: AdapterOptions<T>) {
    this._httpService = inject(HttpService);
    this._api = adapterOptions.api;

    if(adapterOptions.foreignKey) {
      this._foreignKey = adapterOptions.foreignKey;
    }
    if(adapterOptions.fromObject) {
      this._fromObject = adapterOptions.fromObject;
    }
    if(adapterOptions.fromArray) {
      this._fromArray = adapterOptions.fromArray;
    }
    if(adapterOptions.toRemote) {
      this._toRemote = adapterOptions.toRemote;
    }
    if(adapterOptions.toArray) {
      this._toArray = adapterOptions.toArray;
    }
  }

  get foreignKey(): keyof T {
    return this._foreignKey;
  }

  /**
   *
   * @param item
   */
  public async save(item: T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this._httpService.put<any>({
        url: [...this.generateUrl(item), item.uuid],
      }, this._toRemote(item)).pipe(map((_item) => {
        return this._fromObject(_item);
      })).subscribe({
        next: (_item) => {
          resolve(_item);
        },
        error: (_error) => {
          // A cancellation must stay recognizable and not be mapped to an empty SyncException
          if(_error instanceof RequestCancelledError) {
            reject(_error);
            return;
          }
          const syncException = new SyncException();
          if(_error?.status && _error?.message) {
            syncException.code = _error.status;
            syncException.message = _error.message;
          }
          reject(syncException);
        },
        complete: () => {
          // A completion without emission must still settle the promise; after resolve this is a no-op
          reject(new RequestCancelledError());
        }
      });
    });
  }

  /**
   * TODO: Check if there is already the same foreignKey
   *
   * @param items
   */
  public async saveMany(items: T[]): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      if(items.length <= 0) {
        resolve([]);
        return;
      }
      this._httpService.put<any[]>({
        url: [...this.generateUrl(items[0]), 'save-many']
      }, this._toArray(items)).pipe(map((_items) => {
        return this._fromArray(_items);
      })).subscribe({
        next: (_items) => {
          resolve(_items);
        },
        error: (_error) => {
          if(_error instanceof RequestCancelledError) {
            reject(_error);
            return;
          }
          const syncException = new SyncException();
          if(_error?.status && _error?.message) {
            syncException.code = _error.status;
            syncException.message = _error.message;
          }
          reject(syncException);
        },
        complete: () => {
          reject(new RequestCancelledError());
        }
      });
    });
  }

  /**
   *
   * @param syncStatusItem
   */
  public async delete(syncStatusItem: SyncStatusItem): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this._httpService.delete({
        url: [...this._api, syncStatusItem.primaryKey]
      }).subscribe({
        next: () => {
          resolve();
        },
        error: (_error) => {
          if(_error instanceof RequestCancelledError) {
            reject(_error);
            return;
          }
          const syncException = new SyncException();
          if(_error?.status && _error?.message) {
            syncException.code = _error.status;
            syncException.message = _error.message;
          }
          reject(syncException);
        },
        complete: () => {
          reject(new RequestCancelledError());
        }
      });
    });
  }

  /**
   *
   * @param item
   * @private
   */
  protected generateUrl(item: T): string[] {
    return this._api.map(_api => {
      if(this._foreignKey) {
        _api = _api.replace('{foreignKey}', item[this._foreignKey] as string);
      }
      return _api;
    });
  }

}
